import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { DynamicResultTable, FormattedDate, SimpleTD } from '@css-xp/base';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { getTaxExemptionAuditLogs } from '@atlassian/temp-proxies';
import { TaxExemptionAuditLog } from '@atlassian/cssxp-types-billing';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { memoize } from 'lodash';
import React, { useEffect, useState } from 'react';
import './tax-exemption-audit-logs.scss';
import InvoiceGroupIcon from '@css-xp/base/assets/img/icon-folder.svg';
interface Props {
  transactionAccountNumber: string; // Refers transaction Account UUID.
}

const getColumns = memoize(() => {
  const columns = [
    {
      content: 'Date',
      width: 30,
    },
    {
      content: 'Actioned by',
      width: 20,
    },
    {
      content: 'Details',
      width: 50,
    },
  ];

  return {
    cells: columns,
  };
});

export const emptyStateTaxExemptionProp = {
  header: 'Nothing to display yet',
  description: 'No previous details to show',
  // FIXME :: use renderImage instead here
  imageUrl: (InvoiceGroupIcon as unknown) as string,
  testId: 'no-results-tax-exemption-state',
};

export const TaxExemptionAuditLogs: React.FC<Props> = ({ transactionAccountNumber }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState<number>(DEFAULT_PAGE);
  const [taxExemptionLogs, setTaxExemptionLogs] = useState<Array<TaxExemptionAuditLog>>([]);

  const showPagination = taxExemptionLogs.length > DEFAULT_ROWS_PER_PAGE;

  useEffect(() => {
    async function fetchLogs() {
      const taxLogs = await getTaxExemptionAuditLogs(transactionAccountNumber);

      if (taxLogs) {
        setIsLoading(false);
        setTaxExemptionLogs(taxLogs);
      }
    }
    fetchLogs();
  }, []);

  const getLogsBasedOnPage = () => {
    const page = currentPage - 1;
    return taxExemptionLogs.slice(rowsPerPage * page, rowsPerPage * page + rowsPerPage);
  };

  if (isLoading) return <LoadingIndicator />;

  const getCustomHeader = () => {
    return (
      <div className="tax-header">
        <h2>Tax exemption history</h2>
        <h4>Log of exemption requests</h4>
      </div>
    );
  };
  const getRows = () => {
    return getLogsBasedOnPage().map((item, index) => {
      return {
        key: index.toString(),
        cells: [
          {
            content: <FormattedDate date={item.created} showTime={true} />,
          },
          {
            content: <SimpleTD text={item.account?.toString() ?? ''} />,
          },
          {
            content: (
              <div>
                <p>{item.field}</p>
                {item.old_value === null ? 'Not Available' : item.old_value}{' '}
                <ArrowRightIcon size="small" label="Tax Conversion" /> {item.new_value}
              </div>
            ),
          },
        ],
      };
    });
  };

  return (
    <DynamicResultTable
      testId="tax-exemption-logs"
      header={getCustomHeader()}
      head={getColumns()}
      rows={getRows()}
      emptyStateProps={emptyStateTaxExemptionProp}
      isLoading={isLoading}
      totalCount={taxExemptionLogs.length}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={setCurrentPage}
      isSearchEnabled={false}
      isPaginationEnabled={showPagination}
      onRowChange={(value) => {
        setCurrentPage(1);
        setRowsPerPage(value);
      }}
    ></DynamicResultTable>
  );
};
